import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { mergeSx } from '@/utils/mergeSx';

import { Value, SectionValue } from './SectionField';
import { useSectionFieldStyles as useStyles } from './SectionField.styles';
import { SectionFieldValue } from './SectionFieldValue';

type Props = {
	value: Value;
	to?: string | null;
	link?: string | null;
	tooltip?: string | null;
	customSx?: {
		root?: SxProps<Theme>;
		label?: SxProps<Theme>;
		groupName?: SxProps<Theme>;
		value?: SxProps<Theme>;
	};
};

export const SectionFieldContent = ({
	value,
	to,
	link,
	tooltip,
	customSx
}: Props) => {
	const sx = useStyles();
	const { t } = useTranslation();

	if (typeof value === 'string') {
		return (
			<SectionFieldValue to={to} link={link} tooltip={tooltip}>
				{value}
			</SectionFieldValue>
		);
	}

	if (typeof value === 'boolean') {
		return (
			<SectionFieldValue to={to} link={link} tooltip={tooltip}>
				{value ? t('Yes') : t('No')}
			</SectionFieldValue>
		);
	}

	if (isValueArrayOfStrings(value)) {
		return value.map((v, i) => (
			<SectionFieldValue
				key={`type-${i}`}
				to={to}
				link={link}
				tooltip={tooltip}
			>
				{v}
			</SectionFieldValue>
		));
	}

	if (isValueArrayOfObjects(value)) {
		return value.map((v, i) => (
			<SectionFieldValue
				key={`type-${i}`}
				to={to}
				link={v.link ?? link}
				tooltip={tooltip}
				isPrimary={v.isPrimary}
				isSecondary={v.isSecondary}
			>
				{v.name}
			</SectionFieldValue>
		));
	}

	if (isValueOfTypeValue(value)) {
		return (
			<SectionFieldValue
				to={to}
				link={link}
				tooltip={tooltip}
				isPrimary={value.isPrimary}
				isSecondary={value.isSecondary}
			>
				{value.name}
			</SectionFieldValue>
		);
	}

	if (isValueOfTypeGrouped(value)) {
		return Object.entries(value).map(([groupName, specialties], i) => (
			<Fragment key={`type-${i}`}>
				<Typography sx={mergeSx(sx.groupName, customSx?.groupName)}>
					{groupName}
				</Typography>
				{specialties.map((spec, i) => (
					<SectionFieldValue
						key={`type-${groupName}-${i}`}
						to={to}
						link={link}
						tooltip={tooltip}
						isPrimary={spec.isPrimary}
						isSecondary={spec.isSecondary}
					>
						{spec.name}
					</SectionFieldValue>
				))}
			</Fragment>
		));
	}

	return null;
};

const isValueArrayOfStrings = (value: Value): value is string[] =>
	Array.isArray(value) && value.every(v => typeof v === 'string');

const isValueArrayOfObjects = (value: Value): value is SectionValue[] =>
	Array.isArray(value) &&
	value.every(v => typeof v === 'object' && 'name' in v);

const isValueOfTypeValue = (value: Value): value is SectionValue =>
	typeof value === 'object' &&
	value !== null &&
	!Array.isArray(value) &&
	'name' in value;

const isValueOfTypeGrouped = (
	value: Value
): value is Record<string, SectionValue[]> => {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
};
